/**
 * Design Tokens: Colors
 * 
 * Este arquivo contém as definições de cores utilizadas por todos os temas.
 * Segue o princípio do Atomic Design como tokens fundamentais.
 */

export const colors = {
  // Cores comuns
  common: {
    black: '#000000',
    white: '#FFFFFF',
    transparent: 'transparent'
  },

  // Cor de erro (universal para todos os temas)
  // Alinhada com sentiment.negative para consistência visual
  error: {
    main: '#f44336',           // Mesmo vermelho do sentimento negativo
    light: '#e57373',
    dark: '#d32f2f'
  },

  // Paleta Light
  light: {
    primary: '#202124',
    secondary: '#3c4043',
    background: {
      default: '#ffffff',
      paper: '#ffffff'
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#424242'
    },
    divider: 'rgba(0, 0, 0, 0.12)'
  },

  // Paleta Dark
  dark: {
    primary: '#FF4200',
    secondary: '#F96B39',
    background: {
      default: '#121212',
      paper: '#1e1e1e'
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0'
    },
    divider: 'rgba(255, 255, 255, 0.12)'
  },

  // Paleta Moonstone
  moonstone: {
    primary: '#8ab4f8',
    secondary: '#a8c7fa',
    background: {
      default: '#202124',
      paper: '#292a2d'
    },
    text: {
      primary: '#e8eaed',
      secondary: '#9aa0a6'
    },
    divider: 'rgba(255, 255, 255, 0.12)'
  },

  // Paleta Sépia
  sepia: {
    primary: '#8A7B62',           // Bege escuro (ícones, destaques)
    secondary: '#5C4D3C',         // Marrom escuro (complementar)
    background: {
      default: '#F5F3EF',         // Bege claro (fundo)
      paper: '#FDFBF8'            // Bege mais claro (cards)
    },
    text: {
      primary: '#3D3428',         // Marrom escuro (texto principal)
      secondary: '#756C5B'        // Marrom médio (texto secundário)
    },
    divider: 'rgba(90, 77, 60, 0.12)'
  },

  // Paleta Cyber Purple
  cyberPurple: {
    primary: '#3B82F6',           // Azul vibrante
    secondary: '#A855F7',         // Roxo neon
    background: {
      default: '#0f172a',         // Azul muito escuro
      paper: '#1a2332'            // Azul escuro
    },
    text: {
      primary: '#f1f5f9',         // Branco azulado
      secondary: '#94a3b8'        // Cinza azulado
    },
    divider: 'rgba(59, 130, 246, 0.2)'
  },

  // Paleta MIA (Mensure IA)
  mia: {
    primary: '#8B5CF6',           // Roxo vibrante
    secondary: '#6D28D9',         // Roxo escuro
    light: '#A78BFA',             // Roxo claro
    dark: '#5B21B6',              // Roxo mais escuro
    inactive: '#F97316',          // Laranja (quando não ativado)
    inactiveLight: '#FB923C'      // Laranja claro
  },

  // Paleta Instagram
  instagram: {
    primary: '#E1306C',           // Rosa escuro Instagram
    secondary: '#F77737',         // Laranja
    background: {
      default: '#0f0f0f',         // Preto bem escuro
      paper: '#1a1a1a'            // Preto escuro
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0'
    },
    divider: 'rgba(255, 255, 255, 0.12)'
  },

  // Cores de Redes Sociais
  socialMedia: {
    whatsapp: '#25D366',
    telegram: '#0088cc',
    twitter: '#000000',           // Twitter/X
    facebook: '#1877F2',
    linkedin: '#0A66C2',
    instagram: '#E4405F',
    youtube: '#FF0000',
    tiktok: '#000000',
    pinterest: '#E60023',
    gmail: '#EA4335'
  },

  // Cores de Sentimento
  sentiment: {
    positive: {
      main: '#4caf50',           // Verde (Alinhado com SmartScore)
      bg: '#e8f5e9',             // Verde claro para fundo
      light: '#81c784',
      dark: '#388e3c'
    },
    neutral: {
      main: '#9e9e9e',           // Cinza
      bg: '#fff8e1',             // Amarelo claro para fundo
      light: '#bdbdbd',
      dark: '#757575'
    },
    negative: {
      main: '#f44336',           // Vermelho
      bg: '#ffebee',             // Vermelho claro para fundo
      light: '#e57373',
      dark: '#d32f2f'
    }
  },

  // Cores de Exposição
  exposure: {
    dedicated: {
      main: '#4caf50',           // Verde — marca domina
      bg: 'rgba(76, 175, 80, 0.1)',
      light: '#81c784',
      dark: '#388e3c'
    },
    shared: {
      main: '#2196f3',           // Azul — concorrentes presentes
      bg: 'rgba(33, 150, 243, 0.1)',
      light: '#64b5f6',
      dark: '#1976d2'
    },
    mention: {
      main: '#9e9e9e',           // Cinza — menção breve
      bg: 'rgba(158, 158, 158, 0.1)',
      light: '#bdbdbd',
      dark: '#757575'
    },
    none: {
      main: '#9e9e9e',           // Cinza
      bg: 'rgba(158, 158, 158, 0.1)',
      light: '#bdbdbd',
      dark: '#757575'
    }
  },

  // Paleta para Grupos de Contatos
  groupColors: [
    '#667eea', '#764ba2', '#f093fb', '#f5576c',
    '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
    '#fa709a', '#fee140', '#a8edea', '#fed6e3'
  ],

  // Cores de Glassmorphism (para overlays e transparências)
  glass: {
    dark: {
      light: 'rgba(0, 0, 0, 0.5)',
      medium: 'rgba(0, 0, 0, 0.6)',
      heavy: 'rgba(0, 0, 0, 0.7)'
    },
    light: {
      subtle: 'rgba(255, 255, 255, 0.1)',
      light: 'rgba(255, 255, 255, 0.15)',
      medium: 'rgba(255, 255, 255, 0.2)',
      strong: 'rgba(255, 255, 255, 0.3)'
    },
    // Glassmorphism para backgrounds específicos dos temas
    background: {
      darkPaper: 'rgba(30, 30, 30, 0.7)',     // background.paper do dark com 70% opacidade
      lightPaper: 'rgba(255, 255, 255, 0.7)'   // background.paper do light com 70% opacidade
    }
  }
};

/**
 * Gradientes do Design System
 * Usados para elementos especiais como MIA, botões premium, etc.
 */
export const gradients = {
  // Gradiente MIA (Mensure IA) - principal
  mia: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
  miaHover: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
  miaInactive: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',

  // Gradiente Cyber Purple
  cyberPurple: 'linear-gradient(135deg, #3B82F6 0%, #A855F7 100%)',
  cyberPurpleAnimated: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 25%, #4c1d95 50%, #7c3aed 75%, #a855f7 100%)',

  // Gradiente Premium (para elementos especiais)
  premium: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  premiumDark: 'linear-gradient(135deg, #B8860B 0%, #CD853F 100%)',

  // Gradiente Success
  success: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',

  // Gradiente Info
  info: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',

  // Gradiente Instagram (roxoa → pink → laranja)
  instagram: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  instagramSunset: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #F77737 100%)',
  instagramBloom: 'linear-gradient(135deg, #405DE6 0%, #5851DB 25%, #833AB4 50%, #C13584 75%, #E1306C 100%)'
};

/**
 * Cores primárias com gradiente
 * Mapeamento de cores que têm gradiente especial
 */
export const gradientColors = {
  // Roxo
  '#8B5CF6': {
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    label: 'Roxo'
  },
  // Verde
  '#25D366': {
    gradient: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
    label: 'Verde'
  },
  // Colors (Instagram)
  '#E1306C': {
    gradient: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
    label: 'Colors'
  }
};

export default colors;